import { useAppContext } from '@common/contexts/AppContext';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import excludeFalseyValues from '@utils/excludeFalseyValues';
import {
  CalendarAvailabiltyParams,
  getAvailableRoomById,
  getAvailableRooms,
  getCalendarAvailability,
  getRoomExtras,
  getRoomPageSettings,
  getStaticRoomById,
  getStaticRooms,
  RoomParams,
} from './requests';
import { CalendarAvailability } from './types';

type AvailableRoomsParams = Parameters<typeof getAvailableRooms>[0];
type AvailableRoomByIdParams = Omit<Parameters<typeof getAvailableRoomById>[0], 'siteId'>;
type RoomExtrasParams = Omit<Parameters<typeof getRoomExtras>[0], 'siteId'>;

export const roomsKeys = {
  rooms: ['rooms'] as const,
  calendar: () => [...roomsKeys.rooms, 'calendar'] as const,
  calendarAvailability: (params: CalendarAvailabiltyParams & { siteId: number }) =>
    [...roomsKeys.calendar(), params] as const,
  roomSettings: ({ siteId, language }: { siteId: number; language: string }) =>
    [...roomsKeys.rooms, 'settings', { siteId, language }] as const,
  staticRooms: ({ siteId, language }: { siteId: number; language?: string }) =>
    [...roomsKeys.rooms, 'static', { siteId, language }] as const,
  availableRooms: (params: AvailableRoomsParams) =>
    [...roomsKeys.rooms, 'available', excludeFalseyValues(params)] as const,
  staticRoomById: ({ siteId, id, language }: { siteId: number; id: string; language?: string }) =>
    [...roomsKeys.staticRooms({ siteId, language }), id] as const,
  availableRoomById: (params: AvailableRoomByIdParams & { siteId: number }) => {
    const { siteId, id, ...journeyParams } = params;
    return [...roomsKeys.rooms, 'available', siteId, id, excludeFalseyValues(journeyParams)];
  },
  roomExtras: (params: RoomExtrasParams & { siteId: number }) => {
    const { siteId, roomId, rateId, ...journeyParams } = params;
    return [
      ...roomsKeys.rooms,
      'extras',
      siteId,
      roomId,
      rateId,
      excludeFalseyValues(journeyParams),
    ];
  },
};

export function useRoomCalendarAvailabilityForRange(params: CalendarAvailabiltyParams) {
  const { siteId } = useAppContext();

  return useQuery({
    queryKey: roomsKeys.calendarAvailability({ siteId, ...params }),
    queryFn: () => getCalendarAvailability({ siteId, ...params }),
  });
}

export function useRoomCalendarAvailability(params: CalendarAvailabiltyParams) {
  const query = useRoomCalendarAvailabilityForRange(params);

  const client = useQueryClient();
  const availability = client
    .getQueriesData<CalendarAvailability>(roomsKeys.calendar())
    .flatMap(([_, data]) => (data ? data : []));

  return {
    ...query,
    data: availability,
  };
}

export function useRoomPageSettings(language: string, defaultTitle: string, enabled = true) {
  const { siteId } = useAppContext();

  const { data, isFetching } = useQuery({
    queryKey: roomsKeys.roomSettings({ siteId, language }),
    queryFn: () => getRoomPageSettings({ siteId, language }),
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled,
  });

  return {
    content: {
      title: data?.title ?? defaultTitle,
      description: data?.content ?? null,
      heroBackgroundImage: data?.hero_background_image?.url ?? null,
    },
    isFetching,
  };
}

export function useStaticRooms(language?: string) {
  const { siteId } = useAppContext();

  const { data, error, isFetching } = useQuery({
    queryKey: roomsKeys.staticRooms({ siteId, language }),
    queryFn: () => getStaticRooms({ siteId, language }),
    cacheTime: Infinity,
    staleTime: Infinity,
    keepPreviousData: true,
  });

  return {
    data,
    error,
    isFetching,
  };
}

export function useAvailableRooms(params: RoomParams) {
  const { siteId } = useAppContext();
  const isMultiRoomsEnabled =
    useFeatureIsOn('feature.multi-room.enabled') && params.rooms.length > 1;

  const { data, error, isFetching } = useQuery({
    queryKey: roomsKeys.availableRooms({ siteId, isMultiRoomsEnabled, ...params }),
    queryFn: () => getAvailableRooms({ siteId, isMultiRoomsEnabled, ...params }),
    cacheTime: 30 * 1000,
    staleTime: 30 * 1000,
  });

  return {
    data,
    error,
    isFetching,
  };
}

export function useStaticRoomById(id: string, language?: string) {
  const { siteId } = useAppContext();

  const { data, error, isFetching } = useQuery({
    queryKey: roomsKeys.staticRoomById({ siteId, id, language }),
    queryFn: () => getStaticRoomById({ siteId, id, language }),
    cacheTime: Infinity,
    staleTime: Infinity,
  });

  return {
    data,
    error,
    isFetching,
  };
}

export function useAvailableRoomById(params: AvailableRoomByIdParams) {
  const { siteId } = useAppContext();
  const isMultiRoomsEnabled =
    useFeatureIsOn('feature.multi-room.enabled') && params.rooms.length > 1;

  const { data, error, isFetching } = useQuery({
    queryKey: roomsKeys.availableRoomById({ siteId, isMultiRoomsEnabled, ...params }),
    queryFn: () => getAvailableRoomById({ siteId, isMultiRoomsEnabled, ...params }),
    keepPreviousData: true,
  });

  return {
    data,
    error,
    isFetching,
  };
}

export function useRoomExtras(params: RoomExtrasParams) {
  const { siteId } = useAppContext();

  const { data, error, isFetching } = useQuery({
    queryKey: roomsKeys.roomExtras({ siteId, ...params }),
    queryFn: () => getRoomExtras({ siteId, ...params }),
    keepPreviousData: true,
  });

  return {
    data,
    error,
    isFetching,
  };
}
